/**
 * PyramidDSL Compiler (Browser Bundle)
 * 
 * Compiles PyramidDSL source to JSON wire format.
 */

import {
  Token,
  TokenType,
  SourceLocation,
  SourceSpan,
  PyramidFile,
  ScreenDeclaration,
  TypeDeclaration,
  TypeField,
  TypeAnnotation,
  DataBlock,
  DataField,
  ComponentNode,
  PropValue,
  IfNode,
  ForEachNode,
  Expression,
  CompiledScreen,
  CompiledDataSchema,
  JsonNode,
  CompilerError,
  CompilerOutput,
  SlotNode,
} from './types';

// ============================================
// LEXER
// ============================================

const KEYWORDS: Record<string, TokenType> = {
  screen: TokenType.SCREEN,
  data: TokenType.DATA,
  type: TokenType.TYPE,
  if: TokenType.IF,
  else: TokenType.ELSE,
  ForEach: TokenType.FOR_EACH,
  import: TokenType.IMPORT,
  export: TokenType.EXPORT,
  true: TokenType.BOOLEAN,
  false: TokenType.BOOLEAN,
  null: TokenType.NULL,
};

class Lexer {
  private source: string;
  private pos = 0;
  private line = 1;
  private column = 1;
  private tokens: Token[] = [];

  constructor(source: string) {
    this.source = source;
  }

  tokenize(): Token[] {
    while (!this.isAtEnd()) {
      this.scanToken();
    }
    this.addToken(TokenType.EOF, "");
    return this.tokens;
  }

  private scanToken(): void {
    this.skipWhitespaceAndComments();
    if (this.isAtEnd()) return;

    const start = this.location();
    const char = this.advance();

    switch (char) {
      case "{": return this.addToken(TokenType.LBRACE, char, start);
      case "}": return this.addToken(TokenType.RBRACE, char, start);
      case "(": return this.addToken(TokenType.LPAREN, char, start);
      case ")": return this.addToken(TokenType.RPAREN, char, start);
      case "[": return this.addToken(TokenType.LBRACKET, char, start);
      case "]": return this.addToken(TokenType.RBRACKET, char, start);
      case ":": return this.addToken(TokenType.COLON, char, start);
      case ",": return this.addToken(TokenType.COMMA, char, start);
      case ".": return this.addToken(TokenType.DOT, char, start);
      case "@": return this.addToken(TokenType.AT, char, start);
      case "+": return this.addToken(TokenType.PLUS, char, start);
      case "*": return this.addToken(TokenType.STAR, char, start);
      case "%": return this.addToken(TokenType.PERCENT, char, start);
    }

    if (char === "-") {
      if (this.match(">")) return this.addToken(TokenType.ARROW, "->", start);
      return this.addToken(TokenType.MINUS, char, start);
    }

    if (char === "=") {
      if (this.match("=")) return this.addToken(TokenType.DOUBLE_EQUALS, "==", start);
      return this.addToken(TokenType.EQUALS, char, start);
    }

    if (char === "!") {
      if (this.match("=")) return this.addToken(TokenType.NOT_EQUALS, "!=", start);
      return this.addToken(TokenType.NOT, char, start);
    }

    if (char === "<") {
      if (this.match("=")) return this.addToken(TokenType.LESS_EQUALS, "<=", start);
      return this.addToken(TokenType.LESS_THAN, char, start);
    }

    if (char === ">") {
      if (this.match("=")) return this.addToken(TokenType.GREATER_EQUALS, ">=", start);
      return this.addToken(TokenType.GREATER_THAN, char, start);
    }

    if (char === "&" && this.match("&")) return this.addToken(TokenType.AND, "&&", start);
    if (char === "|" && this.match("|")) return this.addToken(TokenType.OR, "||", start);

    if (char === "?") {
      if (this.match("?")) return this.addToken(TokenType.DOUBLE_QUESTION, "??", start);
      return this.addToken(TokenType.QUESTION, char, start);
    }

    if (char === "#") return this.scanColor(start);
    if (char === '"') return this.scanString(start);
    if (this.isDigit(char)) return this.scanNumber(start, char);

    if (char === "$") {
      if (this.match("{")) return this.addToken(TokenType.DOLLAR, "${", start);
      return this.addToken(TokenType.DOLLAR, char, start);
    }

    if (this.isAlpha(char) || char === "_") return this.scanIdentifier(start, char);

    this.addToken(TokenType.ERROR, `Unexpected: ${char}`, start);
  }

  private scanColor(start: SourceLocation): void {
    let value = "#";
    while (this.isHexDigit(this.peek())) value += this.advance();
    this.addToken(TokenType.COLOR, value, start);
  }

  private scanString(start: SourceLocation): void {
    let value = "";
    while (!this.isAtEnd() && this.peek() !== '"') {
      if (this.peek() === "\\") {
        this.advance();
        const escaped = this.advance();
        switch (escaped) {
          case "n": value += "\n"; break;
          case "t": value += "\t"; break;
          case '"': value += '"'; break;
          case "\\": value += "\\"; break;
          case "$": value += "$"; break;
          default: value += escaped;
        }
      } else if (this.peek() === "$" && this.peekNext() === "{") {
        value += this.advance();
        value += this.advance();
      } else {
        value += this.advance();
      }
    }
    if (!this.isAtEnd()) this.advance();
    this.addToken(TokenType.STRING, value, start);
  }

  private scanNumber(start: SourceLocation, first: string): void {
    let value = first;
    while (this.isDigit(this.peek())) value += this.advance();
    if (this.peek() === "." && this.isDigit(this.peekNext())) {
      value += this.advance();
      while (this.isDigit(this.peek())) value += this.advance();
    }
    this.addToken(TokenType.NUMBER, value, start);
  }

  private scanIdentifier(start: SourceLocation, first: string): void {
    let value = first;
    while (this.isAlphaNumeric(this.peek()) || this.peek() === "_") {
      value += this.advance();
    }
    const type = KEYWORDS[value] || TokenType.IDENTIFIER;
    this.addToken(type, value, start);
  }

  private skipWhitespaceAndComments(): void {
    while (!this.isAtEnd()) {
      const char = this.peek();
      if (char === " " || char === "\t" || char === "\r") {
        this.advance();
      } else if (char === "\n") {
        this.line++;
        this.column = 0;
        this.advance();
      } else if (char === "/" && this.peekNext() === "/") {
        while (!this.isAtEnd() && this.peek() !== "\n") this.advance();
      } else if (char === "/" && this.peekNext() === "*") {
        this.advance(); this.advance();
        while (!this.isAtEnd()) {
          if (this.peek() === "*" && this.peekNext() === "/") {
            this.advance(); this.advance();
            break;
          }
          if (this.peek() === "\n") { this.line++; this.column = 0; }
          this.advance();
        }
      } else {
        break;
      }
    }
  }

  private location(): SourceLocation {
    return { line: this.line, column: this.column, offset: this.pos };
  }

  private advance(): string {
    const char = this.source[this.pos];
    this.pos++;
    this.column++;
    return char;
  }

  private peek(): string {
    return this.isAtEnd() ? "\0" : this.source[this.pos];
  }

  private peekNext(): string {
    return this.pos + 1 >= this.source.length ? "\0" : this.source[this.pos + 1];
  }

  private match(expected: string): boolean {
    if (this.isAtEnd() || this.source[this.pos] !== expected) return false;
    this.pos++;
    this.column++;
    return true;
  }

  private isAtEnd(): boolean {
    return this.pos >= this.source.length;
  }

  private isDigit(c: string): boolean {
    return c >= "0" && c <= "9";
  }

  private isAlpha(c: string): boolean {
    return (c >= "a" && c <= "z") || (c >= "A" && c <= "Z");
  }

  private isAlphaNumeric(c: string): boolean {
    return this.isAlpha(c) || this.isDigit(c);
  }

  private isHexDigit(c: string): boolean {
    return this.isDigit(c) || (c >= "a" && c <= "f") || (c >= "A" && c <= "F");
  }

  private addToken(type: TokenType, value: string, start?: SourceLocation): void {
    const startLoc = start || this.location();
    this.tokens.push({
      type,
      value,
      span: { start: startLoc, end: this.location() },
    });
  }
}

// ============================================
// PARSER
// ============================================

class Parser {
  private tokens: Token[];
  private current = 0;
  private errors: CompilerError[] = [];

  constructor(tokens: Token[]) {
    this.tokens = tokens.filter((t) => t.type !== TokenType.ERROR);
  }

  parse(): { ast: PyramidFile; errors: CompilerError[] } {
    const types: TypeDeclaration[] = [];
    const screens: ScreenDeclaration[] = [];

    while (!this.isAtEnd()) {
      try {
        if (this.check(TokenType.TYPE)) {
          types.push(this.parseType());
        } else if (this.check(TokenType.SCREEN)) {
          screens.push(this.parseScreen());
        } else {
          this.advance();
        }
      } catch {
        this.synchronize();
      }
    }

    return {
      ast: {
        kind: "PyramidFile",
        imports: [],
        types,
        screens,
        span: this.fullSpan(),
      },
      errors: this.errors,
    };
  }

  private parseType(): TypeDeclaration {
    const start = this.consume(TokenType.TYPE, "Expected 'type'").span;
    const name = this.consume(TokenType.IDENTIFIER, "Expected type name").value;
    this.consume(TokenType.LBRACE, "Expected '{'");

    const fields: TypeField[] = [];
    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      fields.push(this.parseTypeField());
    }

    this.consume(TokenType.RBRACE, "Expected '}'");

    return { kind: "TypeDeclaration", name, fields, span: this.spanFrom(start) };
  }

  private parseTypeField(): TypeField {
    const start = this.peek().span;
    const name = this.consume(TokenType.IDENTIFIER, "Expected field name").value;
    this.consume(TokenType.COLON, "Expected ':'");
    const type = this.parseTypeAnnotation();
    const optional = type.kind === "OptionalType";

    let defaultValue: Expression | undefined;
    if (this.match(TokenType.EQUALS)) {
      defaultValue = this.parseExpression();
    }

    return { kind: "TypeField", name, type, optional, defaultValue, span: this.spanFrom(start) };
  }

  private parseTypeAnnotation(): TypeAnnotation {
    const start = this.peek().span;
    const name = this.consume(TokenType.IDENTIFIER, "Expected type name").value;

    const primitives = ["String", "Int", "Float", "Bool", "Color", "Date", "DateTime"];
    let type: TypeAnnotation = primitives.includes(name)
      ? { kind: "PrimitiveType", name: name as "String" | "Int" | "Float" | "Bool" | "Color" | "Date" | "DateTime", span: this.spanFrom(start) }
      : { kind: "CustomType", name, span: this.spanFrom(start) };

    if (this.match(TokenType.LBRACKET)) {
      this.consume(TokenType.RBRACKET, "Expected ']'");
      type = { kind: "ArrayType", elementType: type, span: this.spanFrom(start) };
    }

    if (this.match(TokenType.QUESTION)) {
      type = { kind: "OptionalType", innerType: type, span: this.spanFrom(start) };
    }

    return type;
  }

  private parseScreen(): ScreenDeclaration {
    const start = this.consume(TokenType.SCREEN, "Expected 'screen'").span;
    const name = this.consume(TokenType.IDENTIFIER, "Expected screen name").value;

    let route: string | undefined;
    if (this.check(TokenType.LPAREN)) {
      this.advance();
      route = this.consume(TokenType.STRING, "Expected route").value;
      this.consume(TokenType.RPAREN, "Expected ')'");
    }

    this.consume(TokenType.LBRACE, "Expected '{'");

    let dataBlock: DataBlock | undefined;
    if (this.check(TokenType.DATA)) {
      dataBlock = this.parseDataBlock();
    }

    const body = this.parseComponent();
    this.consume(TokenType.RBRACE, "Expected '}'");

    return { kind: "ScreenDeclaration", name, route, dataBlock, body, span: this.spanFrom(start) };
  }

  private parseDataBlock(): DataBlock {
    const start = this.consume(TokenType.DATA, "Expected 'data'").span;
    this.consume(TokenType.LBRACE, "Expected '{'");

    const fields: DataField[] = [];
    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      const fieldStart = this.peek().span;
      const name = this.consume(TokenType.IDENTIFIER, "Expected field name").value;
      this.consume(TokenType.COLON, "Expected ':'");
      const type = this.parseTypeAnnotation();

      let defaultValue: Expression | undefined;
      if (this.match(TokenType.EQUALS)) {
        defaultValue = this.parseExpression();
      }

      fields.push({ kind: "DataField", name, type, defaultValue, span: this.spanFrom(fieldStart) });
    }

    this.consume(TokenType.RBRACE, "Expected '}'");
    return { kind: "DataBlock", fields, span: this.spanFrom(start) };
  }

  private parseComponent(): ComponentNode {
    const start = this.peek().span;
    const type = this.consume(TokenType.IDENTIFIER, "Expected component type").value;

    const props: PropValue[] = [];
    const children: (ComponentNode | IfNode | ForEachNode)[] = [];
    const slots: SlotNode[] = [];

    if (this.match(TokenType.LPAREN)) {
      this.parseComponentProps(props);
      this.consume(TokenType.RPAREN, "Expected ')'");
    }

    if (this.match(TokenType.LBRACE)) {
      while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
        if (this.check(TokenType.IF)) {
          children.push(this.parseIf());
        } else if (this.check(TokenType.FOR_EACH)) {
          children.push(this.parseForEach());
        } else {
          children.push(this.parseComponent());
        }
      }
      this.consume(TokenType.RBRACE, "Expected '}'");
    }

    return { kind: "ComponentNode", type, props, children, slots, span: this.spanFrom(start) };
  }

  private parseComponentProps(props: PropValue[]): void {
    if (!this.check(TokenType.RPAREN) && !this.checkNamedProp()) {
      const value = this.parseExpression();
      props.push({ kind: "PropValue", name: "text", value, span: value.span });
      if (!this.check(TokenType.RPAREN)) this.consume(TokenType.COMMA, "Expected ','");
    }

    while (!this.check(TokenType.RPAREN) && !this.isAtEnd()) {
      const propStart = this.peek().span;
      const name = this.consume(TokenType.IDENTIFIER, "Expected prop name").value;
      this.consume(TokenType.COLON, "Expected ':'");
      const value = this.parseExpression();
      props.push({ kind: "PropValue", name, value, span: this.spanFrom(propStart) });
      if (!this.check(TokenType.RPAREN)) this.consume(TokenType.COMMA, "Expected ','");
    }
  }

  private checkNamedProp(): boolean {
    return this.check(TokenType.IDENTIFIER) && this.tokens[this.current + 1]?.type === TokenType.COLON;
  }

  private parseIf(): IfNode {
    const start = this.consume(TokenType.IF, "Expected 'if'").span;
    const condition = this.parseExpression();
    this.consume(TokenType.LBRACE, "Expected '{'");

    const thenBranch: (ComponentNode | IfNode | ForEachNode)[] = [];
    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      if (this.check(TokenType.IF)) thenBranch.push(this.parseIf());
      else if (this.check(TokenType.FOR_EACH)) thenBranch.push(this.parseForEach());
      else thenBranch.push(this.parseComponent());
    }
    this.consume(TokenType.RBRACE, "Expected '}'");

    let elseBranch: (ComponentNode | IfNode | ForEachNode)[] | undefined;
    if (this.match(TokenType.ELSE)) {
      if (this.check(TokenType.IF)) {
        elseBranch = [this.parseIf()];
      } else {
        this.consume(TokenType.LBRACE, "Expected '{'");
        elseBranch = [];
        while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
          if (this.check(TokenType.IF)) elseBranch.push(this.parseIf());
          else if (this.check(TokenType.FOR_EACH)) elseBranch.push(this.parseForEach());
          else elseBranch.push(this.parseComponent());
        }
        this.consume(TokenType.RBRACE, "Expected '}'");
      }
    }

    return { kind: "IfNode", condition, thenBranch, elseBranch, span: this.spanFrom(start) };
  }

  private parseForEach(): ForEachNode {
    const start = this.consume(TokenType.FOR_EACH, "Expected 'ForEach'").span;
    this.consume(TokenType.LPAREN, "Expected '('");
    const iterable = this.parseExpression();
    this.consume(TokenType.RPAREN, "Expected ')'");
    this.consume(TokenType.LBRACE, "Expected '{'");

    const itemName = this.consume(TokenType.IDENTIFIER, "Expected item name").value;
    let indexName: string | undefined;
    if (this.match(TokenType.COMMA)) {
      indexName = this.consume(TokenType.IDENTIFIER, "Expected index").value;
    }
    this.consume(TokenType.ARROW, "Expected '->'");

    const body: (ComponentNode | IfNode | ForEachNode)[] = [];
    while (!this.check(TokenType.RBRACE) && !this.isAtEnd()) {
      if (this.check(TokenType.IF)) body.push(this.parseIf());
      else if (this.check(TokenType.FOR_EACH)) body.push(this.parseForEach());
      else body.push(this.parseComponent());
    }
    this.consume(TokenType.RBRACE, "Expected '}'");

    return { kind: "ForEachNode", iterable, itemName, indexName, body, span: this.spanFrom(start) };
  }

  private parseExpression(): Expression {
    return this.parseOr();
  }

  private parseOr(): Expression {
    let expr = this.parseAnd();
    while (this.match(TokenType.OR)) {
      const right = this.parseAnd();
      expr = { kind: "BinaryExpr", operator: "||", left: expr, right, span: this.spanFrom(expr.span) };
    }
    return expr;
  }

  private parseAnd(): Expression {
    let expr = this.parseEquality();
    while (this.match(TokenType.AND)) {
      const right = this.parseEquality();
      expr = { kind: "BinaryExpr", operator: "&&", left: expr, right, span: this.spanFrom(expr.span) };
    }
    return expr;
  }

  private parseEquality(): Expression {
    let expr = this.parseComparison();
    while (this.match(TokenType.DOUBLE_EQUALS) || this.match(TokenType.NOT_EQUALS)) {
      const operator = this.previous().value;
      const right = this.parseComparison();
      expr = { kind: "BinaryExpr", operator, left: expr, right, span: this.spanFrom(expr.span) };
    }
    return expr;
  }

  private parseComparison(): Expression {
    let expr = this.parseNullCoalescing();
    while (this.match(TokenType.LESS_THAN) || this.match(TokenType.LESS_EQUALS) ||
           this.match(TokenType.GREATER_THAN) || this.match(TokenType.GREATER_EQUALS)) {
      const operator = this.previous().value;
      const right = this.parseNullCoalescing();
      expr = { kind: "BinaryExpr", operator, left: expr, right, span: this.spanFrom(expr.span) };
    }
    return expr;
  }

  private parseNullCoalescing(): Expression {
    let expr = this.parseTerm();
    while (this.match(TokenType.DOUBLE_QUESTION)) {
      const right = this.parseTerm();
      expr = { kind: "BinaryExpr", operator: "??", left: expr, right, span: this.spanFrom(expr.span) };
    }
    return expr;
  }

  private parseTerm(): Expression {
    let expr = this.parseFactor();
    while (this.match(TokenType.PLUS) || this.match(TokenType.MINUS)) {
      const operator = this.previous().value;
      const right = this.parseFactor();
      expr = { kind: "BinaryExpr", operator, left: expr, right, span: this.spanFrom(expr.span) };
    }
    return expr;
  }

  private parseFactor(): Expression {
    let expr = this.parseUnary();
    while (this.match(TokenType.STAR) || this.match(TokenType.SLASH) || this.match(TokenType.PERCENT)) {
      const operator = this.previous().value;
      const right = this.parseUnary();
      expr = { kind: "BinaryExpr", operator, left: expr, right, span: this.spanFrom(expr.span) };
    }
    return expr;
  }

  private parseUnary(): Expression {
    if (this.match(TokenType.NOT) || this.match(TokenType.MINUS)) {
      const operator = this.previous().value;
      const operand = this.parseUnary();
      return { kind: "UnaryExpr", operator, operand, span: this.spanFrom(operand.span) };
    }
    return this.parsePostfix();
  }

  private parsePostfix(): Expression {
    let expr = this.parsePrimary();

    while (true) {
      if (this.match(TokenType.DOT)) {
        const property = this.consume(TokenType.IDENTIFIER, "Expected property").value;
        expr = { kind: "MemberExpr", object: expr, property, optional: false, span: this.spanFrom(expr.span) };
      } else if (this.match(TokenType.LPAREN)) {
        const args: Expression[] = [];
        if (!this.check(TokenType.RPAREN)) {
          do { args.push(this.parseExpression()); } while (this.match(TokenType.COMMA));
        }
        this.consume(TokenType.RPAREN, "Expected ')'");
        expr = { kind: "CallExpr", callee: expr, arguments: args, span: this.spanFrom(expr.span) };
      } else if (this.match(TokenType.LBRACKET)) {
        this.parseExpression();
        this.consume(TokenType.RBRACKET, "Expected ']'");
      } else {
        break;
      }
    }

    return expr;
  }

  private parsePrimary(): Expression {
    const start = this.peek().span;

    if (this.check(TokenType.BOOLEAN)) {
      const token = this.advance();
      return { kind: "LiteralExpr", value: token.value === "true", literalType: "boolean", span: token.span };
    }

    if (this.match(TokenType.NULL)) {
      return { kind: "LiteralExpr", value: null, literalType: "null", span: start };
    }

    if (this.check(TokenType.NUMBER)) {
      const token = this.advance();
      const value = token.value.includes(".") ? parseFloat(token.value) : parseInt(token.value, 10);
      return { kind: "LiteralExpr", value, literalType: "number", span: token.span };
    }

    if (this.check(TokenType.STRING)) {
      const token = this.advance();
      if (token.value.includes("${")) {
        return this.parseInterpolatedString(token.value, token.span);
      }
      return { kind: "LiteralExpr", value: token.value, literalType: "string", span: token.span };
    }

    if (this.check(TokenType.COLOR)) {
      const token = this.advance();
      return { kind: "LiteralExpr", value: token.value, literalType: "color", span: token.span };
    }

    if (this.match(TokenType.LBRACKET)) {
      const elements: Expression[] = [];
      if (!this.check(TokenType.RBRACKET)) {
        do { elements.push(this.parseExpression()); } while (this.match(TokenType.COMMA));
      }
      this.consume(TokenType.RBRACKET, "Expected ']'");
      return { kind: "ArrayExpr", elements, span: this.spanFrom(start) };
    }

    if (this.match(TokenType.LBRACE)) {
      const properties: { key: string; value: Expression }[] = [];
      if (!this.check(TokenType.RBRACE)) {
        do {
          const key = this.consume(TokenType.IDENTIFIER, "Expected key").value;
          this.consume(TokenType.COLON, "Expected ':'");
          const value = this.parseExpression();
          properties.push({ key, value });
        } while (this.match(TokenType.COMMA));
      }
      this.consume(TokenType.RBRACE, "Expected '}'");
      return { kind: "ObjectExpr", properties, span: this.spanFrom(start) };
    }

    if (this.match(TokenType.LPAREN)) {
      const expr = this.parseExpression();
      this.consume(TokenType.RPAREN, "Expected ')'");
      return expr;
    }

    if (this.check(TokenType.IDENTIFIER)) {
      const token = this.advance();
      return { kind: "IdentifierExpr", name: token.value, span: token.span };
    }

    throw this.error(`Unexpected token: ${this.peek().value}`, "P002", this.peek().span);
  }

  private parseInterpolatedString(value: string, span: SourceSpan): Expression {
    const parts: (string | Expression)[] = [];
    let current = "";
    let i = 0;

    while (i < value.length) {
      if (value[i] === "$" && value[i + 1] === "{") {
        if (current) { parts.push(current); current = ""; }
        let depth = 1;
        let expr = "";
        i += 2;
        while (i < value.length && depth > 0) {
          if (value[i] === "{") depth++;
          if (value[i] === "}") depth--;
          if (depth > 0) expr += value[i];
          i++;
        }
        parts.push({ kind: "IdentifierExpr", name: expr, span } as Expression);
      } else {
        current += value[i];
        i++;
      }
    }
    if (current) parts.push(current);

    return { kind: "InterpolatedStringExpr", parts, span };
  }

  private check(type: TokenType): boolean {
    return !this.isAtEnd() && this.peek().type === type;
  }

  private match(type: TokenType): boolean {
    if (this.check(type)) { this.advance(); return true; }
    return false;
  }

  private advance(): Token {
    if (!this.isAtEnd()) this.current++;
    return this.previous();
  }

  private consume(type: TokenType, message: string): Token {
    if (this.check(type)) return this.advance();
    throw this.error(message, "P003", this.peek().span);
  }

  private peek(): Token {
    return this.tokens[this.current];
  }

  private previous(): Token {
    return this.tokens[this.current - 1];
  }

  private isAtEnd(): boolean {
    return this.peek().type === TokenType.EOF;
  }

  private error(message: string, code: string, span: SourceSpan): Error {
    this.errors.push({ message, code, span, severity: "error" });
    return new Error(message);
  }

  private synchronize(): void {
    this.advance();
    while (!this.isAtEnd()) {
      if (this.check(TokenType.SCREEN) || this.check(TokenType.TYPE)) return;
      this.advance();
    }
  }

  private spanFrom(start: SourceSpan): SourceSpan {
    return { start: start.start, end: this.previous().span.end };
  }

  private fullSpan(): SourceSpan {
    return { start: { line: 1, column: 1, offset: 0 }, end: this.previous().span.end };
  }
}

// ============================================
// EMITTER
// ============================================

const KNOWN_KEYWORDS = new Set([
  "bold", "semibold", "medium", "light", "normal", "thin", "extraBold",
  "center", "start", "end", "top", "bottom", "left", "right",
  "spaceBetween", "spaceAround", "spaceEvenly",
  "fill", "fit", "crop", "inside", "none",
  "ellipsis", "clip", "visible", "scroll",
  "horizontal", "vertical", "true", "false",
]);

class Emitter {
  emit(ast: PyramidFile): CompiledScreen[] {
    return ast.screens.map((screen) => this.emitScreen(screen));
  }

  private emitScreen(screen: ScreenDeclaration): CompiledScreen {
    return {
      name: screen.name,
      route: screen.route,
      data: this.emitDataSchema(screen),
      layout: this.emitComponent(screen.body),
    };
  }

  private emitDataSchema(screen: ScreenDeclaration): CompiledDataSchema {
    if (!screen.dataBlock) return { fields: [] };

    return {
      fields: screen.dataBlock.fields.map((field) => ({
        name: field.name,
        type: this.emitTypeAnnotation(field.type),
        optional: field.type.kind === "OptionalType",
        default: field.defaultValue ? this.evaluateStatic(field.defaultValue) : undefined,
      })),
    };
  }

  private emitTypeAnnotation(type: TypeAnnotation): string {
    switch (type.kind) {
      case "PrimitiveType": return type.name;
      case "CustomType": return type.name;
      case "ArrayType": return `${this.emitTypeAnnotation(type.elementType)}[]`;
      case "OptionalType": return `${this.emitTypeAnnotation(type.innerType)}?`;
      default: return "unknown";
    }
  }

  private emitComponent(node: ComponentNode): JsonNode {
    const result: JsonNode = { type: node.type };

    if (node.props.length > 0) {
      result.props = {};
      for (const prop of node.props) {
        result.props[prop.name] = this.emitExpression(prop.value);
      }
    }

    if (node.children.length > 0) {
      result.children = node.children.map((child) => this.emitChild(child));
    }

    return result;
  }

  private emitChild(child: ComponentNode | IfNode | ForEachNode): JsonNode {
    switch (child.kind) {
      case "ComponentNode": return this.emitComponent(child);
      case "IfNode": return this.emitIf(child);
      case "ForEachNode": return this.emitForEach(child);
    }
  }

  private emitIf(node: IfNode): JsonNode {
    const result: JsonNode = {
      type: "If",
      condition: this.stringifyExpression(node.condition),
      slots: {
        then: node.thenBranch.map((c) => this.emitChild(c)),
      },
    };
    if (node.elseBranch?.length) {
      result.slots!.else = node.elseBranch.map((c) => this.emitChild(c));
    }
    return result;
  }

  private emitForEach(node: ForEachNode): JsonNode {
    return {
      type: "ForEach",
      props: {
        data: this.emitExpression(node.iterable),
        itemName: node.itemName,
        ...(node.indexName && { indexName: node.indexName }),
      },
      children: node.body.map((c) => this.emitChild(c)),
    };
  }

  private emitExpression(expr: Expression): unknown {
    switch (expr.kind) {
      case "LiteralExpr": return expr.value;
      case "IdentifierExpr":
        return KNOWN_KEYWORDS.has(expr.name) ? expr.name : `{{${expr.name}}}`;
      case "MemberExpr":
        return `{{${this.buildMemberPath(expr)}}}`;
      case "InterpolatedStringExpr":
        return expr.parts.map((p) => typeof p === "string" ? p : `{{${this.stringifyExpression(p)}}}`).join("");
      case "ArrayExpr":
        return expr.elements.map((e) => this.emitExpression(e));
      case "ObjectExpr":
        const obj: Record<string, unknown> = {};
        for (const prop of expr.properties) obj[prop.key] = this.emitExpression(prop.value);
        return obj;
      case "CallExpr":
        return { call: this.stringifyExpression(expr.callee), args: expr.arguments.map((a) => this.emitExpression(a)) };
      default:
        return `{{${this.stringifyExpression(expr)}}}`;
    }
  }

  private buildMemberPath(expr: Expression): string {
    if (expr.kind === "IdentifierExpr") return expr.name;
    if (expr.kind === "MemberExpr") {
      const op = expr.optional ? "?." : ".";
      return `${this.buildMemberPath(expr.object)}${op}${expr.property}`;
    }
    return this.stringifyExpression(expr);
  }

  private stringifyExpression(expr: Expression): string {
    switch (expr.kind) {
      case "LiteralExpr":
        return expr.literalType === "string" ? `"${expr.value}"` : String(expr.value);
      case "IdentifierExpr": return expr.name;
      case "MemberExpr":
        return `${this.stringifyExpression(expr.object)}${expr.optional ? "?." : "."}${expr.property}`;
      case "CallExpr":
        return `${this.stringifyExpression(expr.callee)}(${expr.arguments.map((a) => this.stringifyExpression(a)).join(", ")})`;
      case "BinaryExpr":
        return `${this.stringifyExpression(expr.left)} ${expr.operator} ${this.stringifyExpression(expr.right)}`;
      case "UnaryExpr":
        return `${expr.operator}${this.stringifyExpression(expr.operand)}`;
      default: return "";
    }
  }

  private evaluateStatic(expr: Expression): unknown {
    if (expr.kind === "LiteralExpr") return expr.value;
    return null;
  }
}

// ============================================
// PUBLIC API
// ============================================

export function compile(source: string): CompilerOutput {
  const lexer = new Lexer(source);
  const tokens = lexer.tokenize();
  
  const parser = new Parser(tokens);
  const { ast, errors } = parser.parse();
  
  if (errors.length > 0) {
    return { success: false, screens: [], errors, warnings: [] };
  }
  
  const emitter = new Emitter();
  const screens = emitter.emit(ast);
  
  return { success: true, screens, errors: [], warnings: [] };
}

export function formatErrors(errors: CompilerError[], source: string): string {
  const lines = source.split("\n");
  return errors.map((error) => {
    const line = lines[error.span.start.line - 1] || "";
    const marker = " ".repeat(error.span.start.column - 1) + "^";
    return `Error [${error.code}] at line ${error.span.start.line}:\n  ${line}\n  ${marker}\n  ${error.message}`;
  }).join("\n\n");
}

export * from './types';
